import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SButton, SModal } from '@chargebee/sting-react';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';

const InfoModalContext = createContext<{
  open: boolean;
  title: React.ReactNode;
  description: React.ReactNode;
  triggerModal: (args: {
    title: React.ReactNode;
    description: React.ReactNode;
    returnFocusTo?: HTMLElement | null;
  }) => void;
}>(undefined!);

const InfoModal: ReactFCC<{
  onClose: () => void;
  restoreFocus: () => void;
}> = ({ onClose, restoreFocus }) => {
  const { open, title, description } = useContext(InfoModalContext);
  return (
    <SModal open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <SModal.Content
        size="small"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          restoreFocus();
        }}
      >
        <SModal.Header showCloseButton>
          <SModal.Title>{title}</SModal.Title>
        </SModal.Header>
        <SModal.Body>{description}</SModal.Body>
        <SModal.Footer>
          <SButton onClick={onClose}>Got it</SButton>
        </SModal.Footer>
      </SModal.Content>
    </SModal>
  );
};

export const InfoModalContainer: ReactFCC = ({ children }) => {
  const [open, setOpen] = useState(false);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const [title, setTitle] = useState<React.ReactNode>();
  const [description, setDescription] = useState<React.ReactNode>();

  const triggerModal = useCallback(
    (args: {
      title: React.ReactNode;
      description: React.ReactNode;
      returnFocusTo?: HTMLElement | null;
    }) => {
      returnFocusRef.current =
        args.returnFocusTo ??
        (document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null);
      setTitle(args.title);
      setDescription(args.description);
      setOpen(true);
    },
    []
  );

  const value = useMemo(
    () => ({
      open,
      title,
      description,
      triggerModal,
    }),
    [open, title, description, triggerModal]
  );

  return (
    <InfoModalContext.Provider value={value}>
      {children}
      <InfoModal
        onClose={() => setOpen(false)}
        restoreFocus={() => {
          if (returnFocusRef.current?.isConnected)
            returnFocusRef.current.focus();
        }}
      />
    </InfoModalContext.Provider>
  );
};

export const useInfoModal = () => useContext(InfoModalContext);
